import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [activeTab, setActiveTab] = useState<'booking' | 'inquiry'>('booking');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    method: '対面',
    date: '',
    time: '',
    message: ''
  });

  const [inquiryFormData, setInquiryFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [charCount, setCharCount] = useState(0);
  const [inquiryCharCount, setInquiryCharCount] = useState(0);
  const maxChars = 500;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 500) {
      setCharCount(text.length);
    }
  };

  const handleInquiryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'message') {
      if (value.length <= maxChars) {
        setInquiryFormData(prev => ({ ...prev, [name]: value }));
        setInquiryCharCount(value.length);
      }
    } else {
      setInquiryFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inquiryFormData.name || !inquiryFormData.email || !inquiryFormData.phone || !inquiryFormData.subject || !inquiryFormData.message) {
      alert('すべての必須項目を入力してください。');
      return;
    }

    if (inquiryFormData.message.length > maxChars) {
      alert(`お問い合わせ内容は${maxChars}文字以内で入力してください。`);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', inquiryFormData.name);
      formDataToSend.append('email', inquiryFormData.email);
      formDataToSend.append('phone', inquiryFormData.phone);
      formDataToSend.append('subject', inquiryFormData.subject);
      formDataToSend.append('message', inquiryFormData.message);

      const response = await fetch('https://readdy.ai/api/form/d4o26t2jh8g73q73a1e0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitMessage('お問い合わせありがとうございます。内容を確認しまして明日までにご連絡させていただきます。ぽかぽか堂今野');
        setInquiryFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setInquiryCharCount(0);
      } else {
        setSubmitMessage('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitMessage('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setSubmitMessage('メッセージは500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4o0u1ijh8g73q73a130', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitMessage('お問い合わせありがとうございます。内容を確認しまして明日までにご連絡させていただきます。ぽかぽか堂今野');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitMessage('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      setSubmitMessage('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-[#FAF0E6] to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-4">
              相談員について
            </h2>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16 bg-white rounded-3xl p-8 sm:p-12 lg:p-16 shadow-lg">
            <div className="flex-shrink-0">
              <div className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-2xl overflow-hidden shadow-xl">
                <img 
                  alt="相談員 今野直子" 
                  className="w-full h-full object-cover object-top" 
                  src="https://static.readdy.ai/image/a94bf782443eac1605091ed034a69659/4f4763eda849c6de17c5038d6f851d07.jpeg"
                />
              </div>
            </div>
            
            <div className="flex-1 space-y-6 text-center lg:text-left">
              <div className="space-y-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#66BB6A]">今野 直子</h3>
                <div className="w-16 h-1 bg-[#66BB6A] mx-auto lg:mx-0"></div>
              </div>
              
              <div className="space-y-4 text-[#757575] leading-relaxed text-sm sm:text-base">
                <p>
                  通販・輸入物販・ウェブデザイン、メールでの相談活動、など、これまでにさまざまな仕事に携わり、自身でも楽天やAmazonや自店舗ウェブショップを立ち上げ運営してまいりました。
                </p>
                <p>
                  現在、土日限定の自宅敷地内でキッチンカーぽかぽか堂運営しながらこれまでの実務経験をいかし、「はじめの一歩が踏み出せない…」という方のお力になれたらと願い、よりみち相談室をひらきました。
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start pt-4">
                <span className="bg-[#E8F5E9] text-[#66BB6A] px-4 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                  通販・物販経験
                </span>
                <span className="bg-[#E8F5E9] text-[#66BB6A] px-4 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                  ウェブデザイン
                </span>
                <span className="bg-[#E8F5E9] text-[#66BB6A] px-4 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                  相談サポート
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-4 sm:mb-6">
              まずは無料相談から始めませんか？
            </h2>
            <p className="text-base sm:text-lg text-[#757575] max-w-2xl mx-auto">
              対面・オンラインどちらでも対応可能です。お気軽にご相談ください。
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-6 sm:p-10 lg:p-12">
            <div className="flex justify-center mb-8 sm:mb-12">
              <div className="bg-[#F5F5F5] p-1 rounded-full inline-flex gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setActiveTab('booking')}
                  className={`flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer font-medium text-sm sm:text-base ${
                    activeTab === 'booking' ? 'bg-[#66BB6A] text-white' : 'bg-transparent text-[#757575] hover:text-[#2C2C2C]'
                  }`}
                >
                  ご予約
                </button>
                <button
                  onClick={() => setActiveTab('inquiry')}
                  className={`flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer font-medium text-sm sm:text-base ${
                    activeTab === 'inquiry' ? 'bg-[#66BB6A] text-white' : 'bg-transparent text-[#757575] hover:text-[#2C2C2C]'
                  }`}
                >
                  お問い合わせ
                </button>
              </div>
            </div>
            <div className="bg-[#F5F5F5] rounded-3xl p-6 sm:p-10 lg:p-12">
              {activeTab === 'booking' ? (
                <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-readdy-form>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#2C2C2C] mb-2">お名前 *</label>
                      <input
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm"
                        placeholder="山田 太郎"
                        name="name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2C2C2C] mb-2">メールアドレス *</label>
                      <input
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm"
                        placeholder="example@email.com"
                        type="email"
                        name="email"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">電話番号 *</label>
                    <input
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm"
                      placeholder="090-1234-5678"
                      type="tel"
                      name="phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">相談方法 *</label>
                    <select
                      name="type"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="対面">対面</option>
                      <option value="オンライン">オンライン</option>
                    </select>
                  </div>
                  
                  {/* 第1希望 */}
                  <div className="border-t border-gray-300 pt-5">
                    <h3 className="text-base font-semibold text-[#2C2C2C] mb-4">第1希望 *</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#2C2C2C] mb-2">希望日</label>
                        <input
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm cursor-pointer"
                          type="date"
                          name="date1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C2C2C] mb-2">希望時間</label>
                        <input
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm cursor-pointer"
                          type="time"
                          name="time1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 第2希望 */}
                  <div className="border-t border-gray-300 pt-5">
                    <h3 className="text-base font-semibold text-[#2C2C2C] mb-4">第2希望 *</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#2C2C2C] mb-2">希望日</label>
                        <input
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm cursor-pointer"
                          type="date"
                          name="date2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C2C2C] mb-2">希望時間</label>
                        <input
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm cursor-pointer"
                          type="time"
                          name="time2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 第3希望 */}
                  <div className="border-t border-gray-300 pt-5">
                    <h3 className="text-base font-semibold text-[#2C2C2C] mb-4">第3希望 *</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#2C2C2C] mb-2">希望日</label>
                        <input
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm cursor-pointer"
                          type="date"
                          name="date3"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C2C2C] mb-2">希望時間</label>
                        <input
                          required
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all text-sm cursor-pointer"
                          type="time"
                          name="time3"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">ご相談内容</label>
                    <textarea
                      name="message"
                      maxLength={500}
                      rows={4}
                      onChange={handleTextareaChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#81C784] focus:ring-2 focus:ring-[#81C784]/20 outline-none transition-all resize-none text-sm"
                      placeholder="ご相談内容やご質問などがあればお書きください(500文字以内)"
                    ></textarea>
                    <p className="text-xs text-[#757575] mt-1">{charCount} / 500文字</p>
                  </div>
                  {submitMessage && (
                    <div className={`text-center text-sm p-4 rounded-xl ${submitMessage.includes('ありがとうございます') ? 'bg-[#E8F5E9] text-[#2C2C2C]' : 'bg-red-50 text-red-600'}`}>
                      {submitMessage}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#66BB6A] text-white py-3 sm:py-4 rounded-full font-bold hover:bg-[#4CAF50] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer group text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? '送信中...' : '予約を送信する'}
                    {!isSubmitting && <i className="ri-send-plane-fill text-xl group-hover:translate-x-1 transition-transform"></i>}
                  </button>
                </form>
              ) : (
                <form id="inquiry-form" onSubmit={handleInquirySubmit} className="space-y-6" data-readdy-form>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="inquiry-name" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                        お名前 <span className="text-[#E57373]">*</span>
                      </label>
                      <input
                        type="text"
                        id="inquiry-name"
                        name="name"
                        value={inquiryFormData.name}
                        onChange={handleInquiryChange}
                        placeholder="山田 太郎"
                        required
                        className="w-full px-4 py-3 bg-white border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#66BB6A] text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="inquiry-email" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                        メールアドレス <span className="text-[#E57373]">*</span>
                      </label>
                      <input
                        type="email"
                        id="inquiry-email"
                        name="email"
                        value={inquiryFormData.email}
                        onChange={handleInquiryChange}
                        placeholder="example@email.com"
                        required
                        className="w-full px-4 py-3 bg-white border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#66BB6A] text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="inquiry-phone" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="inquiry-phone"
                      name="phone"
                      value={inquiryFormData.phone}
                      onChange={handleInquiryChange}
                      placeholder="090-1234-5678"
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#66BB6A] text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="inquiry-subject" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                      件名 <span className="text-[#E57373]">*</span>
                    </label>
                    <input
                      type="text"
                      id="inquiry-subject"
                      name="subject"
                      value={inquiryFormData.subject}
                      onChange={handleInquiryChange}
                      placeholder="お問い合わせ内容の件名"
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#66BB6A] text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="inquiry-message" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                      お問い合わせ内容 <span className="text-[#E57373]">*</span>
                    </label>
                    <textarea
                      id="inquiry-message"
                      name="message"
                      value={inquiryFormData.message}
                      onChange={handleInquiryChange}
                      rows={6}
                      placeholder="お問い合わせ内容を詳しくお書きください（500文字以内）"
                      required
                      className="w-full px-4 py-3 bg-white border border-[#E0E0E0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#66BB6A] resize-none text-sm"
                    />
                    <div className="text-right text-sm text-[#757575] mt-2">
                      {inquiryCharCount} / {maxChars}文字
                    </div>
                  </div>

                  {submitMessage && (
                    <div className={`text-center text-sm p-4 rounded-xl ${submitMessage.includes('ありがとうございます') ? 'bg-[#E8F5E9] text-[#2C2C2C]' : 'bg-red-50 text-red-600'}`}>
                      {submitMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#66BB6A] text-white py-4 rounded-full font-medium text-base sm:text-lg hover:bg-[#5CAA60] transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? '送信中...' : 'お問い合わせを送信する'}
                    <i className="ri-send-plane-fill"></i>
                  </button>
                </form>
              )}
            </div>

            <div className="mt-8 sm:mt-10 text-center">
              <p className="text-sm sm:text-base text-[#757575] mb-4">
                LINEでもお気軽にお問い合わせいただけます
              </p>
              <a
                href="https://lin.ee/fjJUajb"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 bg-[#06C755] text-white rounded-full font-medium hover:bg-[#05B34C] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-line-fill text-xl"></i>
                LINEで相談する
              </a>
            </div>

            <div className="mt-6 sm:mt-8 text-center">
              <p className="text-sm sm:text-base text-[#757575] mb-4">
                お問い合わせはお電話でもお気軽に
              </p>
              <a
                href="tel:048-992-2286"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 bg-[#66BB6A] text-white rounded-full font-medium hover:bg-[#5CAA60] transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-phone-fill text-xl"></i>
                048-992-2286
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
